import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import TextArea from '../../ui/inputs/TextArea';
import RadioButton from '../../ui/buttons/RadioButton';
import { crossSVG, arrowHeadLeftSVG } from '../../lib/allCustomSVGs';
import '../../styles/components/disconnectionReasonPopup.scss';

interface DisconnectionReasonPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
  onGoBack: () => void;
  isSubmitting?: boolean;
}

const DisconnectionReasonPopup: React.FC<DisconnectionReasonPopupProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onGoBack,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [customReason, setCustomReason] = useState<string>('');

  const predefinedReasons = [
    'I want to take a break',
    'I found a better alternative',
    "It's too expensive",
    "I don't need it anymore",
    'Technical issues',
    'Poor customer support',
    'Other',
  ];

  const handleSubmit = () => {
    const finalReason =
      selectedReason === 'Other' ? customReason : selectedReason;
    if (finalReason.trim()) {
      onSubmit(finalReason);
    }
  };

  const handleClose = () => {
    setSelectedReason('');
    setCustomReason('');
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedReason('');
      setCustomReason('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className='disconnectionReasonPopup'>
      <div
        className='disconnectionReasonPopup__overlay'
        onClick={handleClose}
      ></div>
      <div className='disconnectionReasonPopup__modal'>
        <div className='disconnectionReasonPopup__title'>
          <div className='disconnectionReasonPopup__title--main'>
            <h1 className='disconnectionReasonPopup__title--main-text'>
              {t('Why are you disconnecting?')}
            </h1>
          </div>
        </div>

        <div className='disconnectionReasonPopup__content'>
          <div className='disconnectionReasonPopup__content--description'>
            <p>
              {t(
                'Please let us know why you are disconnecting this integration. This helps us improve our service.'
              )}
            </p>
          </div>

          <div className='disconnectionReasonPopup__content--reasons'>
            {predefinedReasons.map((reason) => (
              <RadioButton
                key={reason}
                label={t(reason)}
                name='disconnectionReason'
                value={reason}
                checked={selectedReason === reason}
                onChange={() => setSelectedReason(reason)}
                disabled={isSubmitting}
              />
            ))}
          </div>

          {selectedReason === 'Other' && (
            <div className='disconnectionReasonPopup__content--customReason'>
              <TextArea
                id='custom-reason-input'
                placeholder={t('Please specify your reason')}
                value={customReason}
                changeListeners={[
                  (event) => setCustomReason(event.target.value),
                ]}
                required={true}
                classes='disconnectionReasonPopup__textarea'
              />
            </div>
          )}
        </div>

        <div className='disconnectionReasonPopup__buttons'>
          <MainSquareButton
            value={t('Go Back')}
            onClick={onGoBack}
            disabled={isSubmitting}
            buttonType='light'
            icon={arrowHeadLeftSVG}
            type='button'
          />
          <MainSquareButton
            value={isSubmitting ? `${t('Disconnecting')}...` : t('Disconnect')}
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              !selectedReason ||
              (selectedReason === 'Other' && !customReason.trim())
            }
            classes='disconnectionReasonPopup__buttons--danger'
            icon={crossSVG}
            type='button'
          />
        </div>
      </div>
    </div>
  );
};

export default DisconnectionReasonPopup;
